import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ShellClientService } from '../../shell-communication/shell-client.service';
import { MessageBusService } from '../../message-bus.service';
import { AdjustmentPanelQueries } from './adjustment-panel-queries';
import { AvailableAdjustmentsReply, GetAvailableAdjustments } from './available-adjustment-operations';
import { IndexedAdjustments, indexAdjustments } from './adjustment-card-reconciliation';
import { getShortUUID } from '../../utils';

// One owner per Hedge Matrix, including before the popup's lazy content exists.
// The launcher and suggestion cards consume the same parsed, book-scoped snapshot.
export class AdjustmentAvailabilityState {
    readonly panelId = getShortUUID();
    readonly changed = new Subject<void>();
    snapshot: IndexedAdjustments = null;
    private readonly end = new Subject<void>();
    private readonly queries = new AdjustmentPanelQueries();
    private owner: string;
    private portfolio: string;
    private generation = 0;
    private flight: Promise<IndexedAdjustments>;
    private timer: any;
    private disposed = false;
    private started = false;
    get count(): number { return this.snapshot?.availableIds.size || 0; }

    constructor(private readonly shell: ShellClientService, private readonly bus: MessageBusService) {}

    start(): void {
        if (this.started || this.disposed) { return; }
        this.started = true;
        ['ActionBoardChangedDto', 'Hg.HedgeMatrixStrikeContextChanged', 'Hg.PortfolioSelected'].forEach(topic =>
            this.bus.of(topic).pipe(takeUntil(this.end)).subscribe(() => this.refresh().catch(() => {})));
        this.timer = setInterval(() => this.refresh().catch(() => {}), 5000);
    }

    setScope(owner: string, portfolio: string, entitled: boolean): void {
        owner = entitled ? owner : null; portfolio = entitled ? portfolio : null;
        if (owner === this.owner && portfolio === this.portfolio) { return; }
        ++this.generation; this.queries.cancelAll(); this.flight = null;
        this.owner = owner; this.portfolio = portfolio; this.snapshot = null; this.changed.next();
        this.refresh().catch(() => {});
    }

    refresh(force = true): Promise<IndexedAdjustments> {
        if (this.disposed || !this.owner || !this.portfolio) { return Promise.resolve(null); }
        if (this.flight) { return this.flight; }
        if (!force && this.snapshot) { return Promise.resolve(this.snapshot); }
        const generation = this.generation, owner = this.owner, portfolio = this.portfolio;
        const work = this.queries.query<AvailableAdjustmentsReply>(cancelled =>
            this.shell.processQuery(new GetAvailableAdjustments(owner, portfolio, this.panelId), '', cancelled))
            .then(reply => {
                if (generation !== this.generation || this.disposed) { return null; }
                this.snapshot = indexAdjustments(reply, owner, portfolio);
                this.changed.next(); return this.snapshot;
            }, error => {
                if (generation === this.generation && !this.disposed) { this.snapshot = null; this.changed.next(); }
                throw error;
            }).finally(() => { if (this.flight === work) { this.flight = null; } });
        this.flight = work; return work;
    }

    dispose(): void {
        this.disposed = true; ++this.generation; clearInterval(this.timer); this.queries.cancelAll();
        this.snapshot = null; this.end.next(); this.end.complete(); this.changed.complete();
    }
}
